import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { MapService } from '../../components/maps/healthcarefinder-map.service';
import { FilterModel } from '../../models/filter.model';
import { Profile } from '../../models/search.model';
import { WebApiModel } from '../../models/web-api.model';
import { FilterService } from '../../services/filter.service';
import { GoogleAnalyticsService } from '../../services/google-analytics.service';
import { LocationService } from '../../services/location.service';
import { RouterHelperService } from '../../services/router-helper.service';
import { SearchService } from '../../services/search.service';
import { SeoService } from '../../services/seo.service';
import { SettingsService } from '../../services/settings.service';
import { StateService } from '../../services/state.service';
import { StateRxService } from '../../services/staterx.service';
import { UiService } from '../../services/ui.service';
import { PopupMapComponent } from '../maps/popup-map/popup-map.component';
import { PopupComponent } from '../popup/popup.component';

const initialSearchTypeParamName = 'initial-search-type';
const initialSearchResultsTypeParamName = 'initial-search-results-type';
const initialServiceSearchTermParamName = 'initial-search-term';
const initialServiceCategorySearchTermParamName = 'initial-search-category-term';
const initialServiceCategoryFilterTermParamName = 'initial-filter-category-term';

@Component({
  selector: 'hcf-search-results',
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SearchResultsComponent implements OnInit, OnDestroy {
  // search terms
  searchTerm: string;
  locationTerm: string;
  nextPage: number;
  searchResultsType: string;
  initialSearchResultsType: string;
  searchType = { profile: 'profile', service: 'service' };
  initialSearchType: string;
  initialSearchTerm: string;
  serviceCategorySearchTerm: string;
  initialServiceCategorySearchTerm: string;
  serviceCategoryFilterTerm: string; // Used to search Service Category Totals separately from results
  initialServiceCategoryFilterTerm: string;
  serviceSearchTerm: string;
  initialServiceSearchTerm: string;
  latitude: string;
  longitude: string;
  myLocationLabel: string;
  affiliatedProvider: boolean;
  affiliatedProviderConsult: boolean;
  easyClaim: boolean;
  telehealth: boolean;
  distance: boolean;
  distanceKms: number;
  defaultDistanceKms: number = 10;
  pageSize: number = 6;
  searchTermDisplay: string;
  serviceDescriptionSelected: string;
  initialSpecialtySearchTerm: string;
  specialtySearchTerm: string;
  specialtySelected: string;

  // search results
  profiles: Profile[];
  places: Profile[];
  placesForMap: WebApiModel.Location[];
  placesForPopupMap: WebApiModel.Location[];
  morePeopleExist: boolean;
  morePlacesExist: boolean;

  // filter variables
  profileTotals: FilterModel.ProfileTotals;
  displayTotals: FilterModel.ProfileTotal;
  displayServiceCategory: FilterModel.ServiceCategory = new FilterModel.ServiceCategory();
  noResultsText: string = SettingsService.values.noResultsText;
  filterType = { people: 'people', places: 'places' };
  selectedFilterType: string = this.filterType.places;
  hasResults: boolean = false; // are there any places and or profiles?
  searchAttempted: boolean = false; // used to stop the Noresults found message showing up at start
  showMoreResults: boolean = false; // whether to show Show More results button when filtering
  isLoading: boolean = false;
  searchFiltersPanelExpanded: boolean = true;
  mobileMapExpanded: boolean = false;
  resetAvailable: boolean = true;
  serviceCategoryDisplay: boolean = false;
  sortRules: { value: string; label: string }[] = [];
  selectedSort: object;
  private _subscriptionOpenPopupMap: Subscription;
  private _subscriptionFindClick: Subscription;
  private _searchSubscription: Subscription;
  mobileVewSize = 768;

  @ViewChild('filterHelpPopupComponent')
  filterHelpPopupComponent: PopupComponent;
  @ViewChild('popupMapComponent') popupMapComponent: PopupMapComponent;

  constructor(
    private _route: ActivatedRoute,
    private _searchService: SearchService,
    private _stateRxService: StateRxService,
    private _stateService: StateService,
    private _filterService: FilterService,
    private _seoService: SeoService,
    private _router: Router,
    private _gaService: GoogleAnalyticsService,
    private _locationService: LocationService,
    private _uiService: UiService,
    private _mapService: MapService,
  ) {
    _router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.indexOf(RouterHelperService.appBaseRoute.searchResults) >= 1) {
          this.initialiseVariables();
          // this needs to be changed to accept two parameters
          this._gaService.sendPageViewEvent(this._router.url);
        }
      }
    });
    this._subscriptionFindClick = _stateRxService.findClick.rxSubscribe(() => {
      this.resetSort();
    });
    this._subscriptionOpenPopupMap = _stateRxService.openPopupMap.rxSubscribe((location) => {
      this.openPopupMap(location);
    });

    // Subscribe to any changes made to the search query parameters and update the search results page accordingly
    this._searchSubscription = this._stateRxService.searchQueryParameters.rxSubscribe(
      (updatedSearchQueryParameters) => {
        this.initializeSearchParameters(updatedSearchQueryParameters);
      },
    );

    if (window.innerWidth > this.mobileVewSize) {
      this.serviceCategoryDisplay = true;
    }
  }

  ngOnInit() {
    this._seoService.setTitle(SettingsService.values.searchResultsTitle);
    void this.initializeSearchParameters(this._stateService.getSearchQueryParameters());

    // Collapse map on mobile by default
    if (this._uiService.isMobile()) {
      this.mobileMapExpanded = false;
    }

    // If browser is resized from mobile to desktop we need to show the map if it was hidden.
    // We need this becasue of ngIf used to collapse map on mobile.
    // We need ngIf to collapse/expand the map because otherwise it's not initialised properly when it's hidden.
    this._stateRxService.mobileLayoutChanged.rxSubscribe((isMobile) => {
      this.mobileMapExpanded = !isMobile;
    });
  }

  ngOnDestroy() {
    this._searchSubscription.unsubscribe();
  }

  // Initializes local search parameters with what's being passed to the search results page
  // from the search input (or home) page
  async initializeSearchParameters(searchQueryParameters: any) {
    this.initialiseVariables();
    this.selectedFilterType = this.filterType.places;
    this.serviceDescriptionSelected = 'false';
    this.specialtySelected = 'false';

    if (!this.isValidSearch(searchQueryParameters)) {
      void this._router.navigateByUrl('/');
      return;
    }

    // Basic search terms

    this.searchTerm = searchQueryParameters[RouterHelperService.urlParam.term];
    this.locationTerm = searchQueryParameters[RouterHelperService.urlParam.location];
    this.latitude = searchQueryParameters[RouterHelperService.urlParam.latitude];
    this.longitude = searchQueryParameters[RouterHelperService.urlParam.longitude];
    this.myLocationLabel = searchQueryParameters[RouterHelperService.urlParam.myLocationLabel];
    // AP and EC checkboxes

    if (
      searchQueryParameters[RouterHelperService.urlParam.affiliatedProvider] ||
      searchQueryParameters[RouterHelperService.urlParam.affiliatedProvider] != null
    ) {
      this.affiliatedProvider = searchQueryParameters[RouterHelperService.urlParam.affiliatedProvider];
    } else {
      this.affiliatedProvider = true;
    }
    if (
      searchQueryParameters[RouterHelperService.urlParam.affiliatedProviderConsult] ||
      searchQueryParameters[RouterHelperService.urlParam.affiliatedProviderConsult] != null
    ) {
      this.affiliatedProviderConsult = searchQueryParameters[RouterHelperService.urlParam.affiliatedProviderConsult];
    } else {
      this.affiliatedProviderConsult = true;
    }
    if (
      searchQueryParameters[RouterHelperService.urlParam.easyClaim] ||
      searchQueryParameters[RouterHelperService.urlParam.easyClaim] != null
    ) {
      this.easyClaim = searchQueryParameters[RouterHelperService.urlParam.easyClaim];
    } else {
      this.easyClaim = true;
    }
    if (
      searchQueryParameters[RouterHelperService.urlParam.telehealth] ||
      searchQueryParameters[RouterHelperService.urlParam.telehealth] != null
    ) {
      this.telehealth = searchQueryParameters[RouterHelperService.urlParam.telehealth];
    } else {
      this.telehealth = true;
    }
    if (
      searchQueryParameters[RouterHelperService.urlParam.distance] ||
      searchQueryParameters[RouterHelperService.urlParam.distance] != null
    ) {
      this.distance = searchQueryParameters[RouterHelperService.urlParam.distance];
      this.distanceKms = searchQueryParameters[RouterHelperService.urlParam.distanceKms];
    } else {
      // By default, enable distance slider if an address/location has been specified
      this.distance = true;
      this.distanceKms = this.defaultDistanceKms;
    }

    // Search type

    this.serviceCategorySearchTerm = searchQueryParameters[RouterHelperService.urlParam.serviceCategory];
    this.serviceSearchTerm = searchQueryParameters[RouterHelperService.urlParam.service];
    this.serviceDescriptionSelected = searchQueryParameters[RouterHelperService.urlParam.serviceSelected];
    this.specialtySearchTerm = searchQueryParameters[RouterHelperService.urlParam.specialty];
    this.specialtySelected = searchQueryParameters[RouterHelperService.urlParam.specialtySelected];

    if (searchQueryParameters[RouterHelperService.urlParam.searchType] === this.filterType.people) {
      this.selectedFilterType = this.filterType.people;
      this.serviceCategoryFilterTerm = searchQueryParameters[RouterHelperService.urlParam.serviceCategoryFilter];
    }
    if (
      searchQueryParameters[RouterHelperService.urlParam.searchType] === this.filterType.places ||
      !searchQueryParameters[RouterHelperService.urlParam.searchType]
    ) {
      this.selectedFilterType = this.filterType.places;
      this.serviceCategoryFilterTerm = searchQueryParameters[RouterHelperService.urlParam.serviceCategoryFilter];
    }

    this.searchResultsType = searchQueryParameters[RouterHelperService.urlParam.searchType];

    if (
      searchQueryParameters[RouterHelperService.urlParam.searchType] ===
      SettingsService.searchResultsType.serviceCategories
    ) {
      // want to search by service categories ONLY
      // this.searchTerm = null; // us12305 - comment this line as we need to remain the searchTerm when filtering the search result.
      this.serviceSearchTerm = null;
      this.serviceCategorySearchTerm = null;
      this.serviceCategoryFilterTerm = searchQueryParameters[RouterHelperService.urlParam.term];
    } else if (
      searchQueryParameters[RouterHelperService.urlParam.searchType] ===
      SettingsService.searchResultsType.serviceCategory
    ) {
      // want to search by service category ONLY
      // this.searchTerm = null; // us12305 - comment this line as we need to remain the searchTerm when filtering the search result.
      this.serviceSearchTerm = null;
      this.serviceCategorySearchTerm = searchQueryParameters[RouterHelperService.urlParam.term];
      this.serviceCategoryFilterTerm = null;
    } else if (
      searchQueryParameters[RouterHelperService.urlParam.searchType] === SettingsService.searchResultsType.services
    ) {
      // want to search by service ONLY
      // this.searchTerm = null; // us12305 - comment this line as we need to remain the searchTerm when filtering the search result.
      this.serviceSearchTerm = searchQueryParameters[RouterHelperService.urlParam.term];
      this.serviceCategorySearchTerm = null;
      this.serviceCategoryFilterTerm = null;
    } else if (
      searchQueryParameters[RouterHelperService.urlParam.searchType] === SettingsService.searchResultsType.service
    ) {
      // want to search by service ONLY
      // this.searchTerm = null; // us12305 - comment this line as we need to remain the searchTerm when filtering the search result.
      this.serviceSearchTerm = searchQueryParameters[RouterHelperService.urlParam.term];
      this.serviceCategorySearchTerm = null;
      this.serviceCategoryFilterTerm = null;
      this.serviceDescriptionSelected = 'true';
    } else if (
      searchQueryParameters[RouterHelperService.urlParam.searchType] === SettingsService.searchResultsType.specialties
    ) {
      // want to search by service ONLY
      // this.searchTerm = null; // us12305 - comment this line as we need to remain the searchTerm when filtering the search result.
      this.specialtySearchTerm = searchQueryParameters[RouterHelperService.urlParam.term];
      this.serviceCategorySearchTerm = null;
      this.serviceCategoryFilterTerm = null;
      this.initSelectedFilterType(searchQueryParameters);
    } else if (
      searchQueryParameters[RouterHelperService.urlParam.searchType] === SettingsService.searchResultsType.specialty
    ) {
      // want to search by service ONLY
      // this.searchTerm = null; // us12305 - comment this line as we need to remain the searchTerm when filtering the search result.
      this.specialtySearchTerm = searchQueryParameters[RouterHelperService.urlParam.term];
      this.serviceCategorySearchTerm = null;
      this.serviceCategoryFilterTerm = null;
      this.specialtySelected = 'true';
      this.initSelectedFilterType(searchQueryParameters);
    }

    // Initial search settings

    this.initialSearchTerm = this.searchTerm;
    if (searchQueryParameters[initialSearchTypeParamName]) {
      this.initialSearchType = searchQueryParameters[initialSearchTypeParamName];
    } else {
      if (
        this.serviceCategorySearchTerm ||
        this.serviceCategoryFilterTerm ||
        this.serviceSearchTerm ||
        this.specialtySearchTerm
      ) {
        this.initialSearchType = this.searchType.service;
      } else {
        this.initialSearchType = this.searchType.profile;
      }
    }

    if (searchQueryParameters[initialServiceSearchTermParamName]) {
      this.initialServiceSearchTerm = this.noneToNull(searchQueryParameters[initialServiceSearchTermParamName]);
    } else {
      this.initialServiceSearchTerm = this.serviceSearchTerm;
    }

    if (searchQueryParameters[initialServiceCategorySearchTermParamName]) {
      this.initialServiceCategorySearchTerm = this.noneToNull(
        searchQueryParameters[initialServiceCategorySearchTermParamName],
      );
    } else {
      this.initialServiceCategorySearchTerm = this.serviceCategorySearchTerm;
    }

    if (searchQueryParameters[initialServiceCategoryFilterTermParamName]) {
      this.initialServiceCategoryFilterTerm = this.noneToNull(
        searchQueryParameters[initialServiceCategoryFilterTermParamName],
      );
    } else {
      this.initialServiceCategoryFilterTerm = this.serviceCategoryFilterTerm;
    }

    if (searchQueryParameters[initialSearchResultsTypeParamName]) {
      this.initialSearchResultsType = this.noneToNull(searchQueryParameters[initialSearchResultsTypeParamName]);
    } else {
      this.initialSearchResultsType = this.searchResultsType;
    }

    this.setResetAvailable();
    this.setSearchTermDisplay();

    // Retrieve lat/lng of specified location
    await this.setLatLngFromLocation();

    this.initialSortValue(this.locationTerm, this.latitude, this.longitude);

    // Initiate the search and update the page

    if (this.initialSearchType === this.searchType.service) {
      this.getServiceCategoryFilterData();
      this.getDataByService();
      this.getLocationsByService();
    } else {
      this.getFilterData();
      this.getData();
      this.getLocations();
    }
  }

  private initSelectedFilterType(searchQueryParameters: any) {
    switch (searchQueryParameters[RouterHelperService.urlParam.defaultCategory]) {
      case this.filterType.people:
        this.selectedFilterType = this.filterType.people;
        break;
      case this.filterType.places:
        this.selectedFilterType = this.filterType.places;
        break;
      default:
        this.selectedFilterType = this.filterType.places;
    }
  }
  // Retrieve lat/lng of specified location
  private async setLatLngFromLocation() {
    if (this.locationTerm && this.locationTerm.length > 0) {
      const loc = await this._mapService.getLocationFromAddress(this.locationTerm + ', New Zealand');

      if (loc) {
        this.latitude = loc.lat.toString();
        this.longitude = loc.lng.toString();
      }
    }
  }

  // Checks if the search operation is valid
  // For a search operation to be valid it has to have a search term
  private isValidSearch(searchParameters: any): boolean {
    if (searchParameters != null && searchParameters[RouterHelperService.urlParam.term]) {
      return true;
    }
    return false;
  }

  // Constructs the text that needs to be displayed in the search results page as the page header
  private setSearchTermDisplay() {
    let text = '';
    const provideMatchingService = ' providing services matching ';
    const matching = ' matching ';
    const quote = "'";
    if (this.selectedFilterType === this.filterType.people) {
      text = 'People';
    } else {
      text = 'Places';
    }

    if (this.serviceSearchTerm) {
      text += provideMatchingService + quote + this.serviceSearchTerm + quote;
    } else if (this.serviceCategorySearchTerm) {
      text += provideMatchingService + quote + this.serviceCategorySearchTerm + quote;
    } else if (this.serviceCategoryFilterTerm) {
      text += provideMatchingService + quote + this.serviceCategoryFilterTerm + quote;
    } else {
      text += matching + quote + this.searchTerm + quote;
    }

    if (this.locationTerm) {
      text += ' in ' + quote + this.locationTerm + quote;
    }

    this.searchTermDisplay = text;
  }

  onDistanceUpdate(event: any) {
    // Update distance shown in interface as user slides

    this.distanceKms = event.value;
  }

  onDistanceChange(event: any) {
    // Check the "include nearby locations" box
    this.distance = true;

    // Perform search
    if (!this.isLoading) {
      this.isLoading = true;
      this.reroute();
    }
  }

  // Decides if the reset button is available in the search results page
  private setResetAvailable() {
    this.resetAvailable = false;

    if ((this.searchTerm || this.initialSearchTerm) && this.searchTerm !== this.initialSearchTerm) {
      this.resetAvailable = true;
    } else if (
      (this.serviceSearchTerm || this.initialServiceSearchTerm) &&
      this.serviceSearchTerm !== this.initialServiceSearchTerm
    ) {
      this.resetAvailable = true;
    } else if (
      (this.specialtySearchTerm || this.initialSpecialtySearchTerm) &&
      this.serviceSearchTerm !== this.initialServiceSearchTerm
    ) {
      this.resetAvailable = true;
    } else if (
      (this.serviceCategorySearchTerm || this.initialServiceCategorySearchTerm) &&
      this.serviceCategorySearchTerm !== this.initialServiceCategorySearchTerm
    ) {
      this.resetAvailable = true;
    } else if (
      (this.serviceCategoryFilterTerm || this.initialServiceCategoryFilterTerm) &&
      this.serviceCategoryFilterTerm !== this.initialServiceCategoryFilterTerm
    ) {
      this.resetAvailable = true;
    }
  }

  private initialiseVariables() {
    this.nextPage = 1;
    this.profiles = [];
    this.places = [];
  }

  private selectServiceCategorySearchTerm(): string {
    if (this.serviceCategorySearchTerm) {
      return '%' + this.serviceCategorySearchTerm + '%';
    } else if (this.serviceCategoryFilterTerm) {
      return '%' + this.serviceCategoryFilterTerm + '%';
    }

    return null;
  }

  private selectServiceSearchTerm(): string {
    if (this.initialSearchResultsType === SettingsService.searchResultsType.services) {
      if (this.serviceDescriptionSelected === 'true') {
        return this.serviceSearchTerm;
      }
      return '%' + this.serviceSearchTerm + '%';
    }

    return this.serviceSearchTerm;
  }

  private selectSpecialtySearchTerm(): string {
    /* It seems as if adding the wildcards caused false positives.
      Business was concerned that a search for urologist returned neurologist as well.
      Keeping the code commented out until after testing. If successful, the code will
      be removed entirely
    */
    // if (this.initialSearchResultsType === SettingsService.searchResultsType.specialties) {
    //   if (this.specialtySelected === 'true') {
    //     return this.specialtySearchTerm;
    //   }
    //   return '%' + this.specialtySearchTerm + '%';
    // }

    return this.specialtySearchTerm;
  }

  private getFilterData() {
    this.showMoreResults = false;

    this._filterService
      .getTotals(
        this.searchTerm,
        this.locationTerm,
        this.affiliatedProvider,
        this.affiliatedProviderConsult,
        this.easyClaim,
        this.telehealth,
        this.latitude,
        this.longitude,
        this.distance,
        this.distanceKms,
        this.selectServiceCategorySearchTerm(),
        this.selectServiceSearchTerm(),
        this.selectSpecialtySearchTerm(),
      )
      .subscribe((response: FilterModel.ProfileTotals) => {
        this.profileTotals = response;
        this.setFilterType(this.selectedFilterType, false);
      });
  }

  resetFilter(): void {
    this.searchTerm = this.initialSearchTerm;
    this.serviceSearchTerm = this.initialServiceSearchTerm;
    this.serviceCategorySearchTerm = this.initialServiceCategorySearchTerm;
    this.serviceCategoryFilterTerm = this.initialServiceCategoryFilterTerm;
    this.serviceDescriptionSelected = 'false';
    this.specialtySelected = 'false';
    this.reroute();
  }

  unsetService(): void {
    if (this.serviceSearchTerm) {
      if (this.initialServiceSearchTerm && this.serviceSearchTerm === this.initialServiceSearchTerm) {
        this.searchTerm = this.initialSearchTerm;
        this.serviceSearchTerm = this.initialServiceSearchTerm;
        this.serviceCategorySearchTerm = this.initialServiceCategorySearchTerm;
        this.serviceCategoryFilterTerm = this.initialServiceCategoryFilterTerm;
      } else {
        this.serviceDescriptionSelected = 'false';
        this.serviceSearchTerm = this.initialServiceSearchTerm;
      }
      this.reroute();
    } else if (this.specialtySearchTerm) {
      if (this.initialSpecialtySearchTerm && this.specialtySearchTerm === this.initialSpecialtySearchTerm) {
        this.searchTerm = this.initialSearchTerm;
        this.specialtySearchTerm = this.initialSpecialtySearchTerm;
      } else {
        this.specialtySelected = 'false';
        // this.specialtySearchTerm = this.initialSpecialtySearchTerm;
      }
      this.serviceCategorySearchTerm = this.initialServiceCategorySearchTerm;
      this.serviceCategoryFilterTerm = this.initialServiceCategoryFilterTerm;
      this.reroute();
    } else if (this.serviceCategorySearchTerm) {
      this.resetFilter();
    }
  }

  filterByServiceCategory(serviceCategory: string): void {
    this.serviceCategorySearchTerm = serviceCategory;
    this.reroute();
  }

  getServiceCategoryFilterData() {
    this._filterService
      .getServiceTotals(
        this.searchTerm,
        this.locationTerm,
        this.affiliatedProvider,
        this.affiliatedProviderConsult,
        this.easyClaim,
        this.telehealth,
        this.latitude,
        this.longitude,
        this.distance,
        this.distanceKms,
        this.selectServiceCategorySearchTerm(),
        this.selectServiceSearchTerm(),
        this.selectSpecialtySearchTerm(),
      )
      .subscribe((response: FilterModel.ProfileTotals) => {
        this.profileTotals = response;
        this.setFilterType(this.selectedFilterType, false);
      });
  }

  filterByService(service: string): void {
    this.serviceSearchTerm = service;
    this.serviceDescriptionSelected = 'true';
    this.reroute();
  }

  getDataScroll() {
    if (
      (this.selectedFilterType === this.filterType.people && !this.morePeopleExist) ||
      (this.selectedFilterType === this.filterType.places && !this.morePlacesExist) ||
      (!this.morePeopleExist && !this.morePlacesExist)
    ) {
      return;
    }

    if (this.initialSearchType === this.searchType.service) {
      this.getDataByService();
    } else {
      this.getData();
    }
  }

  getData(): void {
    this.getPeopleAndPlaces(
      this.searchTerm,
      this.locationTerm,
      this.affiliatedProvider,
      this.affiliatedProviderConsult,
      this.selectedSort['value'],
      this.easyClaim,
      this.telehealth,
      this.latitude,
      this.longitude,
      this.distance,
      this.distanceKms,
    );
    this.nextPage++;
  }

  getDataByService(): void {
    this.getPeopleAndPlacesByService(
      this.searchTerm,
      this.locationTerm,
      this.affiliatedProvider,
      this.affiliatedProviderConsult,
      this.selectedSort['value'],
      this.easyClaim,
      this.telehealth,
      this.latitude,
      this.longitude,
      this.distance,
      this.distanceKms,
    );
    if (this.displayTotals && this.displayTotals.serviceCategoryTotals) {
      this.displayServiceCategory = this.displayTotals.serviceCategoryTotals.find(
        (x) => x.name === this.serviceCategorySearchTerm,
      );
    }
    this.nextPage++;
  }

  setFilterType(type: string, getLocations: boolean): void {
    this.selectedFilterType = type;
    this.showMoreResults = false;
    if (type === this.filterType.people) {
      if (this.profileTotals) {
        this.displayTotals = this.profileTotals.peopleTotals;
        this.hasResults = this.profileTotals.peopleTotals.total > 0;
      }
      this.showMoreResults = this.morePeopleExist;
    } else if (type === this.filterType.places) {
      if (this.profileTotals) {
        this.displayTotals = this.profileTotals.placesTotals;
        this.hasResults = this.profileTotals.placesTotals.total > 0;
      }
      this.showMoreResults = this.morePlacesExist;
    }

    if (this.displayTotals && this.displayTotals.serviceCategoryTotals) {
      this.displayServiceCategory = this.displayTotals.serviceCategoryTotals.find(
        (x) => x.name === this.serviceCategorySearchTerm,
      );
    }

    if (getLocations) {
      if (this.initialSearchType === this.searchType.service) {
        this.getLocationsByService();
      } else {
        this.getLocations();
      }
    }

    this.setSearchTermDisplay();
  }

  setFilterEC(isEasyClaimChecked: boolean): void {
    this.isLoading = true;
    this.easyClaim = !this.easyClaim;
    this.reroute();
  }

  setFilterTelehealth(isTelehealthChecked: boolean): void {
    this.isLoading = true;
    this.telehealth = !this.telehealth;
    this.reroute();
  }

  setFilterAP(isAffiliatedProviderChecked: boolean): void {
    this.isLoading = true;
    this.affiliatedProvider = !this.affiliatedProvider;
    if (!isAffiliatedProviderChecked) {
      this.affiliatedProviderConsult = false;
      this.telehealth = false;
    } else {
      this.affiliatedProviderConsult = true;
      this.telehealth = true;
    }
    this.reroute();
  }

  setFilterAPConsult(isAffiliatedProviderConsulChecked: boolean): void {
    this.isLoading = true;
    this.affiliatedProviderConsult = !this.affiliatedProviderConsult;
    this.reroute();
  }

  setFilterDistanceKms(isDistanceKmsChecked: boolean): void {
    this.isLoading = true;
    this.distance = !this.distance;
    this.reroute();
  }

  private getLocations() {
    this._locationService
      .getLocations(
        this.searchTerm,
        this.locationTerm,
        this.affiliatedProvider,
        this.affiliatedProviderConsult,
        this.selectedSort['value'],
        this.easyClaim,
        this.telehealth,
        this.latitude,
        this.longitude,
        this.distance,
        this.distanceKms,
        this.selectServiceCategorySearchTerm(),
        this.selectServiceSearchTerm(),
        this.selectSpecialtySearchTerm(),
        this.selectedFilterType === this.filterType.people,
      )
      .subscribe((response: WebApiModel.Location[]) => {
        this.placesForMap = response;
      });
  }

  private getLocationsByService() {
    this._locationService
      .getLocationsByService(
        this.searchTerm,
        this.selectServiceCategorySearchTerm(),
        this.selectServiceSearchTerm(),
        this.selectSpecialtySearchTerm(),
        this.locationTerm,
        this.affiliatedProvider,
        this.affiliatedProviderConsult,
        this.selectedSort['value'],
        this.easyClaim,
        this.telehealth,
        this.latitude,
        this.longitude,
        this.distance,
        this.distanceKms,
        this.selectedFilterType === this.filterType.people,
      )
      .subscribe((response: WebApiModel.Location[]) => {
        this.placesForMap = response;
      });
  }
  private sortByAlphabetAscend(profiles: Profile[]) {
    profiles.sort((a, b) => a.type.localeCompare(b.type));
    profiles.sort((a, b) => {
      if (a.type === 'people') {
        if ((<WebApiModel.Person>a.data).firstName < (<WebApiModel.Person>b.data).firstName) {
          return -1;
        }
        if ((<WebApiModel.Person>a.data).firstName > (<WebApiModel.Person>b.data).firstName) {
          return 1;
        }
        return 0;
      }
      if (a.type === 'places') {
        if ((<WebApiModel.Place>a.data).practiceName < (<WebApiModel.Place>b.data).practiceName) {
          return -1;
        }
        if ((<WebApiModel.Place>a.data).practiceName > (<WebApiModel.Place>b.data).practiceName) {
          return 1;
        }
        return 0;
      }
      return 0;
    });
  }
  private sortByAlphabetDescend(profiles: Profile[]) {
    profiles.sort((a, b) => a.type.localeCompare(b.type));
    profiles.sort((a, b) => {
      if (a.type === 'people') {
        if ((<WebApiModel.Person>a.data).firstName > (<WebApiModel.Person>b.data).firstName) {
          return -1;
        }
        if ((<WebApiModel.Person>a.data).firstName < (<WebApiModel.Person>b.data).firstName) {
          return 1;
        }
        return 0;
      }
      if (a.type === 'places') {
        if ((<WebApiModel.Place>a.data).practiceName > (<WebApiModel.Place>b.data).practiceName) {
          return -1;
        }
        if ((<WebApiModel.Place>a.data).practiceName < (<WebApiModel.Place>b.data).practiceName) {
          return 1;
        }
        return 0;
      }
      return 0;
    });
  }
  private sortByNearestLocation(profiles: Profile[]) {
    profiles.sort((a, b) => a.type.localeCompare(b.type));
    profiles.sort((a, b) => {
      if (a.type === 'places') {
        return (<WebApiModel.Place>a.data).distance - (<WebApiModel.Place>b.data).distance;
      }
      return 0;
    });
  }
  sortChange() {
    switch (this.selectedSort) {
      case this.sortRules[0]:
        this.reroute();
        break;
      case this.sortRules[1]:
        this.reroute();
        break;
      case this.sortRules[2]:
        this.reroute();
        break;
    }
  }
  private resetSort() {
    if ((this.latitude && this.longitude) || this.locationTerm) {
      this.sortRules = [
        { value: 'ascend', label: 'Ascending (A to Z)' },
        { value: 'descend', label: 'Descending (Z to A)' },
        { value: 'location', label: 'Nearest to location' },
      ];
      this.selectedSort = this.sortRules[2];
    } else {
      this.sortRules = [
        { value: 'ascend', label: 'Ascending (A to Z)' },
        { value: 'descend', label: 'Descending (Z to A)' },
      ];
      this.selectedSort = this.sortRules[0];
    }
  }

  private initialSortValue(location: string, latitude: string, longitude: string) {
    if ((latitude && longitude) || location) {
      this.sortRules = [
        { value: 'ascend', label: 'Ascending (A to Z)' },
        { value: 'descend', label: 'Descending (Z to A)' },
        { value: 'location', label: 'Nearest to location' },
      ];
      if (!this.selectedSort) {
        this.selectedSort = this.sortRules[2];
      }
    } else {
      this.sortRules = [
        { value: 'ascend', label: 'Ascending (A to Z)' },
        { value: 'descend', label: 'Descending (Z to A)' },
      ];
      if (!this.selectedSort || (this.selectedSort && this.selectedSort['value'] === 'location')) {
        this.selectedSort = this.sortRules[0];
      }
    }
  }
  private getPeopleAndPlaces(
    name: string,
    location: string,
    affiliatedProvider: boolean,
    affiliatedProviderConsult: boolean,
    sort: string,
    easyClaim: boolean,
    telehealth: boolean,
    latitude: string,
    longitude: string,
    distance: boolean,
    distanceKms: number,
  ): void {
    // set loading so spinner shows if necessary
    this.isLoading = true;
    this._searchService
      .getPeopleAndPlaces(
        name,
        location,
        affiliatedProvider,
        affiliatedProviderConsult,
        sort,
        easyClaim,
        telehealth,
        this.nextPage,
        this.pageSize,
        this.selectServiceCategorySearchTerm(),
        this.selectServiceSearchTerm(),
        this.selectSpecialtySearchTerm(),
        latitude,
        longitude,
        distance,
        distanceKms,
      )
      .subscribe(
        (response: Profile[][]) => {
          this.isLoading = false;
          this.profiles = this.profiles.concat(response[0]);
          this.morePeopleExist = response[0].length === this.pageSize;
          this.places = this.places.concat(response[1]);
          this.profiles = this.profiles.concat(response[1]);
          this.morePlacesExist = response[1].length === this.pageSize;
          this.searchAttempted = true;
          const lastDefaultCategory = this._stateService.getLastDefaultCategory();
          if (lastDefaultCategory) {
            this.setFilterType(lastDefaultCategory.toLowerCase(), false);
          } else {
            this.setFilterType(this.selectedFilterType, false);
          }
          const locationInput: string = location ? location : latitude && longitude ? this.myLocationLabel : null;
          if (this.profiles && this.profiles.length <= 0) {
            this._gaService.sendNoSearchResultsEvent(name, locationInput);
          } else {
            this._gaService.sendSearchResultsEvent(name, locationInput);
          }
        },
        () => {
          this.isLoading = false;
        },
      );
  }

  private getPeopleAndPlacesByService(
    name: string,
    location: string,
    affiliatedProvider: boolean,
    affiliatedProviderConsult: boolean,
    sort: string,
    easyClaim: boolean,
    telehealth: boolean,
    latitude: string,
    longitude: string,
    distance: boolean,
    distanceKms: number,
  ): void {
    // set loading so spinner shows if necesssary
    this.isLoading = true;
    this._searchService
      .getPeopleAndPlacesByService(
        name,
        this.selectServiceCategorySearchTerm(),
        this.selectServiceSearchTerm(),
        this.selectSpecialtySearchTerm(),
        location,
        affiliatedProvider,
        affiliatedProviderConsult,
        sort,
        easyClaim,
        telehealth,
        this.nextPage,
        this.pageSize,
        latitude,
        longitude,
        distance,
        distanceKms,
      )
      .subscribe(
        (response: Profile[][]) => {
          this.isLoading = false;
          this.profiles = this.profiles.concat(response[0]);
          this.morePeopleExist = response[0].length === this.pageSize;
          this.places = this.places.concat(response[1]);
          this.profiles = this.profiles.concat(response[1]);
          this.morePlacesExist = response[1].length === this.pageSize;
          this.searchAttempted = true;
          const lastDefaultCategory = this._stateService.getLastDefaultCategory();
          if (lastDefaultCategory) {
            this.setFilterType(lastDefaultCategory.toLowerCase(), false);
          } else {
            this.setFilterType(this.selectedFilterType, false);
          }
          this._stateService.clearLastDefaultCategory();
          // this.hasResults = this.profiles && this.profiles.length > 0;
          // Either service category or service will have a value but not both
          // The values seem to be prefixed and suffixed with % and %dietitian% would become NaNietitian cause of %d
          // Hence the character replacement before sending the event to Google Analytics
          let gaEventTerm =
            this.selectServiceCategorySearchTerm() ||
            this.selectServiceSearchTerm() ||
            this.selectSpecialtySearchTerm();
          gaEventTerm = gaEventTerm.replace(/%/g, '');
          const locationInput: string = location ? location : latitude && longitude ? this.myLocationLabel : null;
          if (this.profiles && this.profiles.length <= 0) {
            this._gaService.sendNoSearchResultsEvent(gaEventTerm, locationInput);
          } else {
            this._gaService.sendSearchResultsEvent(gaEventTerm, locationInput);
          }
          this.setFilterType(this.selectedFilterType, false);
        },
        () => {
          this.isLoading = false;
        },
      );
  }

  toggleSearchFiltersPanel() {
    this.searchFiltersPanelExpanded = !this.searchFiltersPanelExpanded;
  }

  toggleMap() {
    this.mobileMapExpanded = !this.mobileMapExpanded;
    this.openPopupMap(null);
  }

  shouldShowSlider() {
    return (this.displayTotals && this.locationTerm != null) || (this.displayTotals && this.latitude && this.longitude);
  }

  noneToNull(mightBeNone: string): string {
    if (mightBeNone === 'none') {
      return null;
    } else {
      return mightBeNone;
    }
  }

  nullToNone(mightBeNull: string): string {
    if (!mightBeNull) {
      return 'none';
    } else {
      return mightBeNull;
    }
  }

  private reroute() {
    const urlParams = {};

    urlParams[RouterHelperService.urlParam.term] = this.searchTerm;
    urlParams[RouterHelperService.urlParam.location] = this.locationTerm;
    urlParams[RouterHelperService.urlParam.searchType] = this.selectedFilterType;
    urlParams[RouterHelperService.urlParam.latitude] = this.latitude;
    urlParams[RouterHelperService.urlParam.longitude] = this.longitude;
    urlParams[RouterHelperService.urlParam.serviceCategory] = this.serviceCategorySearchTerm;
    urlParams[RouterHelperService.urlParam.serviceCategoryFilter] = this.serviceCategoryFilterTerm;
    urlParams[RouterHelperService.urlParam.service] = this.serviceSearchTerm;
    urlParams[RouterHelperService.urlParam.specialty] = this.specialtySearchTerm;
    urlParams[RouterHelperService.urlParam.easyClaim] = this.easyClaim;
    urlParams[RouterHelperService.urlParam.telehealth] = this.telehealth;
    urlParams[RouterHelperService.urlParam.distance] = this.distance;
    urlParams[RouterHelperService.urlParam.distanceKms] = this.distance ? this.distanceKms : this.defaultDistanceKms;
    urlParams[RouterHelperService.urlParam.affiliatedProvider] = this.affiliatedProvider;
    urlParams[RouterHelperService.urlParam.affiliatedProviderConsult] = this.affiliatedProviderConsult;
    urlParams[RouterHelperService.urlParam.sort] = this.selectedSort['value'];
    urlParams[initialSearchTypeParamName] = this.initialSearchType;
    urlParams[RouterHelperService.urlParam.serviceSelected] = this.serviceDescriptionSelected;
    urlParams[RouterHelperService.urlParam.specialtySelected] = this.specialtySelected;
    urlParams[initialSearchResultsTypeParamName] = this.nullToNone(this.initialSearchResultsType);
    urlParams[initialServiceSearchTermParamName] = this.nullToNone(this.initialServiceSearchTerm);
    urlParams[initialServiceCategorySearchTermParamName] = this.nullToNone(this.initialServiceCategorySearchTerm);
    urlParams[initialServiceCategoryFilterTermParamName] = this.nullToNone(this.initialServiceCategoryFilterTerm);

    const routeUrl = RouterHelperService.getRoute(RouterHelperService.routeType.searchResults, null, null, urlParams);

    // Will save the query string collection to the newly added searchParameters and then later remove queryParams from navigate()

    this._stateService.setSearchQueryParameters(routeUrl.query);
    void this._router.navigate(routeUrl.link);
    this._uiService.scrollToTop();
  }

  showFilterHelp() {
    this.filterHelpPopupComponent.openPopup();
  }

  openPopupMap(places: WebApiModel.Location[]) {
    if (places) {
      this.placesForPopupMap = places;
    } else {
      this.placesForPopupMap = this.placesForMap;
    }
    this.popupMapComponent.openPopupMap();
  }

  closePopupMap() {
    this.popupMapComponent.closePopupMap();
  }

  closePopup() {
    this.filterHelpPopupComponent.closePopup();
  }

  toggleServiceCategory(serviceCategoryDisplay: boolean) {
    this.serviceCategoryDisplay = !this.serviceCategoryDisplay;
  }
}
